import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../services/api';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import QuickActions from '../components/dashboard/QuickActions';
import QualityHealth from '../components/dashboard/QualityHealth';
import KpiRow from '../components/dashboard/KpiRow';
import SecondaryStatsStrip from '../components/dashboard/SecondaryStatsStrip';
import ExecutiveInsights, { Insight } from '../components/dashboard/ExecutiveInsights';
import AttentionRequired, { AttentionItem } from '../components/dashboard/AttentionRequired';
import QualityTrendChart from '../components/dashboard/QualityTrendChart';
import AIProductivitySummary from '../components/dashboard/AIProductivitySummary';
import ProjectQualityTable from '../components/dashboard/ProjectQualityTable';
import TestingActivitySummary from '../components/dashboard/TestingActivitySummary';
import QAProductivitySummary from '../components/dashboard/QAProductivitySummary';
import RecentActivityFeed from '../components/dashboard/RecentActivityFeed';
import RecentTestRuns from '../components/dashboard/RecentTestRuns';
import './DashboardPage.css';

interface StatsResponse {
  kpis: {
    total_projects: number;
    total_test_cases: number;
    total_scenarios: number;
    total_runs: number;
    completed_runs: number;
  };
  execution_summary: { success_rate: number; total_executed: number; blocked_percentage: number };
  quality_health: { status: string; reason: string };
  critical_failures: number;
  coverage: { percentage: number };
  comparisons: { success_rate_delta: number | null; coverage_delta: number | null };
  recent_runs: any[];
  execution_trend: any[];
}

interface InsightsResponse {
  insights: Insight[];
  attention_required: AttentionItem[];
}

export const DashboardPage: React.FC = () => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [insightsData, setInsightsData] = useState<InsightsResponse | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(true);

  const loadStats = useCallback((selectedDays: number) => {
    setStatsLoading(true);
    apiClient.get(`/api/v1/dashboard/stats?days=${selectedDays}`)
      .then(resp => {
        setStats(resp.data as StatsResponse);
        setLastUpdated(new Date());
      })
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  const loadInsights = useCallback((selectedDays: number) => {
    setInsightsLoading(true);
    apiClient.get(`/api/v1/dashboard/insights?days=${selectedDays}`)
      .then(resp => setInsightsData(resp.data as InsightsResponse))
      .catch(() => setInsightsData(null))
      .finally(() => setInsightsLoading(false));
  }, []);

  useEffect(() => { loadStats(days); }, [days, loadStats]);
  useEffect(() => { loadInsights(days); }, [days, loadInsights]);

  const handleRefresh = () => { loadStats(days); loadInsights(days); };

  return (
    <div className="dashboard-page-v2">
      <DashboardHeader
        days={days}
        onDaysChange={setDays}
        onRefresh={handleRefresh}
        lastUpdated={lastUpdated}
        refreshing={statsLoading}
      />

      <QuickActions />

      <QualityHealth
        status={stats?.quality_health.status}
        reason={stats?.quality_health.reason}
        loading={statsLoading}
        indicators={[
          { label: 'Pass Rate', value: stats ? `${stats.execution_summary.success_rate}%` : '—', tooltip: '% of executed tests that passed in the selected period.' },
          { label: 'Critical', value: stats ? `${stats.critical_failures}` : '—', tooltip: 'Critical-priority test failures in the selected period.' },
          { label: 'Blocked', value: stats ? `${stats.execution_summary.blocked_percentage}%` : '—', tooltip: '% of executed tests that are currently blocked.' },
          { label: 'Coverage', value: stats ? `${stats.coverage.percentage}%` : '—', tooltip: '% of scenarios executed at least once, ever (all-time, not period-limited).' },
        ]}
      />

      <KpiRow stats={stats} loading={statsLoading} days={days} />
      <SecondaryStatsStrip kpis={stats?.kpis ?? null} loading={statsLoading} />

      <div className="dpv2-two-col-60-40">
        <ExecutiveInsights insights={insightsData?.insights ?? []} loading={insightsLoading} />
        <AttentionRequired items={insightsData?.attention_required ?? []} loading={insightsLoading} />
      </div>

      <QualityTrendChart />

      <div className="dpv2-two-col-40-60">
        <AIProductivitySummary days={days} />
        <ProjectQualityTable />
      </div>

      <div className="dpv2-three-col">
        <TestingActivitySummary />
        <QAProductivitySummary />
        <RecentActivityFeed />
      </div>

      <RecentTestRuns runs={stats?.recent_runs ?? []} loading={statsLoading} />
    </div>
  );
};

export default DashboardPage;
